import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    icon: '🎵',
    title: 'Share a moment',
    desc: 'Found a part of a song that gives you chills? Pin the exact second and share it with the world.',
  },
  {
    icon: '🔥',
    title: 'React & vibe',
    desc: 'React with 8 emotions, drop a comment, and connect with people who feel the same way.',
  },
  {
    icon: '🎧',
    title: 'Start an Echo Room',
    desc: 'Create a room, invite friends, and listen to the exact same moment — all in sync with live chat.',
  },
  {
    icon: '🎶',
    title: 'Save to collections',
    desc: 'Curate your favourite moments into collections and share your taste with the world.',
  },
];

export default function WelcomeModal({ onClose, username }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleAbout = () => {
    onClose();
    navigate('/about');
  };

  return (
    <div style={styles.overlay}>
      <style>{`
        @keyframes wm-in {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wm-step {
          from { opacity: 0; transform: translateX(16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .wm-next:hover { background: #c0456f !important; }
        .wm-skip:hover { color: rgba(245,230,237,0.6) !important; }
        .wm-about:hover { color: #D4537E !important; }
      `}</style>

      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>♪</div>
            <span style={styles.logoText}>Echo Moments</span>
          </div>
          <button className="wm-skip" onClick={onClose} style={styles.skipBtn}>
            Skip
          </button>
        </div>

        {/* Welcome line */}
        <div style={styles.welcomeLine}>
          <p style={styles.welcomeText}>
            Welcome{username ? `, ${username}` : ''}! 👋
          </p>
          <p style={styles.welcomeSub}>Here's everything you can do</p>
        </div>

        {/* Step content */}
        <div key={step} style={styles.stepWrap}>
          <div style={styles.stepIcon}>{STEPS[step].icon}</div>
          <div style={styles.stepTitle}>{STEPS[step].title}</div>
          <div style={styles.stepDesc}>{STEPS[step].desc}</div>
        </div>

        {/* Dots */}
        <div style={styles.dots}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              onClick={() => setStep(i)}
              style={{
                ...styles.dot,
                background: i === step
                  ? '#D4537E'
                  : 'rgba(212,83,126,0.2)',
                width: i === step ? 20 : 6,
              }}
            />
          ))}
        </div>

        {/* Actions */}
        <button className="wm-next" onClick={handleNext} style={styles.nextBtn}>
          {isLast ? "Let's go ♪" : 'Next →'}
        </button>

        <button className="wm-about" onClick={handleAbout} style={styles.aboutBtn}>
          Learn more about EchoMoments ↗
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(8,6,8,0.85)',
    backdropFilter: 'blur(12px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  },
  card: {
    background: '#0d0a0f',
    border: '0.5px solid rgba(212,83,126,0.25)',
    borderRadius: 20,
    padding: '32px 28px',
    maxWidth: 400,
    width: '100%',
    animation: 'wm-in 0.4s ease-out forwards',
    textAlign: 'center',
  },
  header: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  logo: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  logoIcon: {
    width: 26, height: 26, borderRadius: '50%',
    background: 'linear-gradient(135deg, #D4537E, #993556)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, color: '#fff',
  },
  logoText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 16, fontWeight: 300,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: '#f5e6ed',
  },
  skipBtn: {
    background: 'none', border: 'none',
    fontSize: 11, letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(245,230,237,0.25)',
    cursor: 'pointer', transition: 'color 0.2s',
  },
  welcomeLine: {
    marginBottom: 28,
  },
  welcomeText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 26, fontWeight: 300,
    color: '#f5e6ed', marginBottom: 4,
  },
  welcomeSub: {
    fontSize: 12, color: 'rgba(245,230,237,0.35)',
    letterSpacing: '0.1em', textTransform: 'uppercase',
  },
  stepWrap: {
    animation: 'wm-step 0.3s ease-out forwards',
    marginBottom: 28,
    minHeight: 140,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  stepIcon: {
    fontSize: 40, marginBottom: 14,
  },
  stepTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22, fontWeight: 400,
    color: '#f5e6ed', marginBottom: 10,
  },
  stepDesc: {
    fontSize: 13, color: 'rgba(245,230,237,0.5)',
    lineHeight: 1.7, maxWidth: 280, margin: '0 auto',
  },
  dots: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 6,
    marginBottom: 24,
  },
  dot: {
    height: 6, borderRadius: 3,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  nextBtn: {
    width: '100%',
    background: '#D4537E', color: '#fff',
    border: 'none', borderRadius: 12,
    padding: '12px 0', fontSize: 13,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    cursor: 'pointer', marginBottom: 12,
    transition: 'background 0.2s',
    fontFamily: "'DM Sans', sans-serif",
  },
  aboutBtn: {
    background: 'none', border: 'none',
    fontSize: 11, color: 'rgba(245,230,237,0.3)',
    letterSpacing: '0.06em', cursor: 'pointer',
    transition: 'color 0.2s',
  },
};